import React, { useCallback, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import CustomerCombobox from '../../components/CustomerCombobox.jsx';
import MasterCombobox from '../../components/MasterCombobox';
import { Package, Users, MapPin, Tag, FileText, Info } from 'lucide-react';

const COURIER_PKG_TYPES = ['Envelope', 'Document', 'Box', 'Flyer', 'Carton Box', 'Parcel', 'Pallet', 'Wooden Box', 'Others'];
const COURIER_SERVICE_LEVELS = ['DTD (Door To Door)', 'Express', 'Priority', 'Standard', 'Economy', 'Same Day'];
const CARGO_NATURE_OPTIONS = ['General', 'Documents', 'Commercial Sample', 'Perishable', 'Valuable', 'Non HAZ Temp', 'HAZ'];

const Radio = ({ name, value, checked, onChange, label }) => (
  <label className="flex items-center gap-1.5 cursor-pointer text-xs font-ibm font-medium text-slate-700">
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="accent-amber-600 w-3.5 h-3.5"
    />
    {label}
  </label>
);

const SectionHeader = ({ icon: Icon, color, title, subtitle }) => (
  <div className={`border-b-4 ${color} px-5 py-3 flex items-center justify-between`}>
    <div className="flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-slate-800" />
      <h2 className="text-xs font-bold tracking-wider font-ibm uppercase text-slate-900">{title}</h2>
    </div>
    {subtitle && <span className="text-[11px] font-ibm text-slate-400">{subtitle}</span>}
  </div>
);

const F = ({ label, required, children, hint }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <Label className="text-[11px] font-semibold font-ibm text-gray-700 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {hint && <span className="text-[10px] text-slate-400 font-ibm">{hint}</span>}
    </div>
    {children}
  </div>
);

const CourierDocketForm = ({
  payload = {},
  onChange,
  customers = [],
  customerDetails = {},
  onCustomerSelect,
  locations = [],
  carriers = [],
}) => {
  const h = payload.header || {};
  const p = payload.package_details || {};
  const pt = payload.party_details || {};
  const iv = payload.invoice_details || {};
  const ship = payload.shipment_info || {};
  const pInfo = payload.package_info || {};

  // Comprehensive update that synchronizes all standard fields for DocketPrint compatibility
  const updateFields = useCallback((updates) => {
    const next = { ...payload };

    if (updates.header) {
      next.header = { ...(next.header || {}), ...updates.header };
    }
    if (updates.party_details) {
      next.party_details = { ...(next.party_details || {}), ...updates.party_details };
    }
    if (updates.shipment_info) {
      next.shipment_info = { ...(next.shipment_info || {}), ...updates.shipment_info };
    }
    if (updates.package_details) {
      next.package_details = { ...(next.package_details || {}), ...updates.package_details };
    }
    if (updates.package_info) {
      next.package_info = { ...(next.package_info || {}), ...updates.package_info };
    }
    if (updates.invoice_details) {
      next.invoice_details = { ...(next.invoice_details || {}), ...updates.invoice_details };
    }
    if (updates.invoices) {
      next.invoices = updates.invoices;
    }

    onChange(next);
  }, [payload, onChange]);

  // Set default movement & service values on initial load if blank
  useEffect(() => {
    const needDefaults = !h.inward_outward || !h.international_domestic || !h.service_type || !h.commercial_type;
    if (needDefaults) {
      updateFields({
        header: {
          inward_outward: h.inward_outward || 'Outward',
          international_domestic: h.international_domestic || 'International',
          service_type: h.service_type || 'DTD (Door To Door)',
          commercial_type: h.commercial_type || 'Commercial',
          cargo_type: h.cargo_type || 'General',
          package_type: h.package_type || 'Parcel',
        },
        package_details: {
          package_type: p.package_type || 'Parcel',
          no_of_packages: p.no_of_packages || 1,
        },
        package_info: {
          package_no_of_packages: pInfo.package_no_of_packages || 1,
        }
      });
    }
  }, []); // eslint-disable-line

  // Auto-fill pickup_address from consignor's branch
  useEffect(() => {
    const cid = pt.consignor || h.exporter_shipper;
    if (!cid || !customerDetails[cid]) return;
    const branches = customerDetails[cid].branches || [];
    if (!branches.length) return;
    const b = branches.find(x => x.is_head_office) || branches[0];
    const addr = [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode, b.country].filter(Boolean).join(', ');
    if (addr && !pt.pickup_address) {
      updateFields({
        party_details: { pickup_address: addr, consignor_phone: b.contact_person ? `${b.contact_person} (${b.contact_numbers || ''})` : '' }
      });
    }
  }, [pt.consignor, h.exporter_shipper, customerDetails]); // eslint-disable-line

  // Auto-fill delivery_address from consignee's branch
  useEffect(() => {
    const cid = pt.consignee || h.importer_receiver;
    if (!cid || !customerDetails[cid]) return;
    const branches = customerDetails[cid].branches || [];
    if (!branches.length) return;
    const b = branches.find(x => x.is_head_office) || branches[0];
    const addr = [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode, b.country].filter(Boolean).join(', ');
    if (addr && !pt.delivery_address) {
      updateFields({
        party_details: { delivery_address: addr, consignee_phone: b.contact_person ? `${b.contact_person} (${b.contact_numbers || ''})` : '' }
      });
    }
  }, [pt.consignee, h.importer_receiver, customerDetails]); // eslint-disable-line

  const getBranches = (cid) => {
    if (!cid || !customerDetails[cid]) return [];
    return (customerDetails[cid].branches || []).map(b => ({
      id: b.id,
      name: b.branch_nick_name || b.city || 'Branch',
      subtext: [b.city, b.state].filter(Boolean).join(', ')
    }));
  };

  const locItems = locations.map(l => ({
    id: l.id,
    name: `${l.location_name}${l.location_code ? ' (' + l.location_code + ')' : ''}`,
    subtext: [l.city, l.country].filter(Boolean).join(', ')
  }));

  // Filter or prioritize courier carriers
  const courierCarriers = carriers.filter(c => c.carrier_type === 'COURIER');
  const otherCarriers = carriers.filter(c => c.carrier_type !== 'COURIER');
  const carrierOptions = [...courierCarriers, ...otherCarriers];

  return (
    <div className="space-y-5" data-testid="courier-docket-form">
      {/* ─── SECTION 1: COURIER SERVICE & MOVEMENT DETAILS ──────────────── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <SectionHeader
          icon={Package}
          color="border-amber-500"
          title="Courier Service & Movement"
          subtitle="Simple courier dispatch configuration"
        />
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-center bg-slate-50/70 p-3.5 rounded-lg border border-slate-100">
            {/* Movement Direction */}
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold font-ibm text-gray-700 uppercase tracking-wide">
                Movement Direction <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-4 pt-1">
                <Radio
                  name="inward_outward"
                  value="Outward"
                  checked={(h.inward_outward || 'Outward') === 'Outward'}
                  onChange={v => updateFields({ header: { inward_outward: v } })}
                  label="Outward"
                />
                <Radio
                  name="inward_outward"
                  value="Inward"
                  checked={h.inward_outward === 'Inward'}
                  onChange={v => updateFields({ header: { inward_outward: v } })}
                  label="Inward"
                />
              </div>
            </div>

            {/* Scope */}
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold font-ibm text-gray-700 uppercase tracking-wide">
                Shipment Scope <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-4 pt-1">
                <Radio
                  name="intl_dom"
                  value="International"
                  checked={(h.international_domestic || 'International') === 'International'}
                  onChange={v => updateFields({ header: { international_domestic: v } })}
                  label="International"
                />
                <Radio
                  name="intl_dom"
                  value="Domestic"
                  checked={h.international_domestic === 'Domestic'}
                  onChange={v => updateFields({ header: { international_domestic: v } })}
                  label="Domestic"
                />
              </div>
            </div>

            {/* Commercial Type */}
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold font-ibm text-gray-700 uppercase tracking-wide">
                Commercial Type
              </Label>
              <div className="flex items-center gap-4 pt-1">
                <Radio
                  name="commercial_type"
                  value="Commercial"
                  checked={(h.commercial_type || 'Commercial') === 'Commercial'}
                  onChange={v => updateFields({ header: { commercial_type: v } })}
                  label="Commercial"
                />
                <Radio
                  name="commercial_type"
                  value="Non Commercial"
                  checked={h.commercial_type === 'Non Commercial'}
                  onChange={v => updateFields({ header: { commercial_type: v } })}
                  label="Non-Comrcl"
                />
              </div>
            </div>

            {/* Service Level */}
            <F label="Service Level">
              <Select
                value={h.service_type || 'DTD (Door To Door)'}
                onValueChange={v => updateFields({ header: { service_type: v } })}
              >
                <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COURIER_SERVICE_LEVELS.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            {/* Courier Partner / Carrier */}
            <F label="Courier Partner / Carrier" hint="Select service provider">
              <Select
                value={ship.carrier || 'none'}
                onValueChange={v => {
                  const val = v === 'none' ? '' : v;
                  updateFields({
                    shipment_info: { carrier: val },
                    header: { carrier: val }
                  });
                }}
              >
                <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white" data-testid="courier-carrier-select">
                  <SelectValue placeholder="-- Select Courier Partner --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Courier Partner --</SelectItem>
                  {carrierOptions.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.carrier_name} {c.carrier_type === 'COURIER' ? '⚡' : ''} ({c.carrier_code || c.carrier_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>

            {/* Courier Tracking / AWB No */}
            <F label="Courier AWB / Tracking Ref No" hint="Carrier tracking number">
              <Input
                data-testid="courier-tracking-input"
                value={ship.carrier_docket_no || h.carrier_docket_no || ''}
                onChange={e => {
                  const val = e.target.value;
                  updateFields({
                    shipment_info: { carrier_docket_no: val },
                    header: { carrier_docket_no: val }
                  });
                }}
                placeholder="e.g. 781234567890 / BD987654"
                className="h-8 text-xs border-gray-300 font-mono"
              />
            </F>

            {/* Booking / Dispatch Date */}
            <F label="Booking / Dispatch Date">
              <Input
                type="date"
                value={h.docket_date || ''}
                onChange={e => updateFields({ header: { docket_date: e.target.value } })}
                className="h-8 text-xs border-gray-300 font-ibm"
              />
            </F>
          </div>
        </div>
      </Card>

      {/* ─── SECTION 2: PARTIES (SHIPPER & CONSIGNEE) ───────────────────── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <SectionHeader
          icon={Users}
          color="border-blue-500"
          title="Parties & Addresses"
          subtitle="Consignor, Consignee and Billing Party"
        />
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Consignor / Shipper (FROM) */}
            <div className="space-y-3 p-4 bg-orange-50/30 rounded-xl border border-orange-100">
              <div className="flex items-center justify-between pb-2 border-b border-orange-200/60">
                <span className="text-xs font-bold text-orange-900 font-ibm uppercase tracking-wide">
                  From: Shipper / Consignor
                </span>
                <span className="text-[10px] text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded font-medium">Origin</span>
              </div>

              <F label="Shipper Name" required>
                <CustomerCombobox
                  items={customers}
                  value={pt.consignor || h.exporter_shipper || ''}
                  onSelect={item => {
                    const cid = item ? item.id : '';
                    if (onCustomerSelect && cid) onCustomerSelect(cid, 'consignor');
                    updateFields({
                      party_details: {
                        consignor: cid,
                        consignor_name: item ? item.business_name : '',
                        consignor_branch: '',
                        pickup_address: '',
                        bill_to: pt.bill_to ? pt.bill_to : cid,
                      },
                      header: {
                        exporter_shipper: cid,
                        exporter_shipper_branch: '',
                        bill_to: h.bill_to ? h.bill_to : cid,
                      }
                    });
                  }}
                  placeholder="Select Sender / Shipper"
                />
              </F>

              {/* Branch Selector */}
              {getBranches(pt.consignor || h.exporter_shipper).length > 0 && (
                <F label="Branch / Facility">
                  <Select
                    value={pt.consignor_branch || h.exporter_shipper_branch || 'none'}
                    onValueChange={v => {
                      const bid = v === 'none' ? '' : v;
                      const branches = customerDetails[pt.consignor || h.exporter_shipper]?.branches || [];
                      const b = branches.find(x => x.id === bid);
                      const addr = b ? [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode, b.country].filter(Boolean).join(', ') : pt.pickup_address;
                      updateFields({
                        party_details: {
                          consignor_branch: bid,
                          pickup_address: addr || pt.pickup_address,
                          consignor_phone: b?.contact_person ? `${b.contact_person} (${b.contact_numbers || ''})` : pt.consignor_phone
                        },
                        header: { exporter_shipper_branch: bid }
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Default Head Office --</SelectItem>
                      {getBranches(pt.consignor || h.exporter_shipper).map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} ({b.subtext})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
              )}

              <F label="Pickup / Sender Address">
                <Textarea
                  rows={2}
                  value={pt.pickup_address || ''}
                  onChange={e => updateFields({ party_details: { pickup_address: e.target.value } })}
                  placeholder="Full pickup street address, city, pin code..."
                  className="text-xs border-gray-300 font-ibm resize-none bg-white"
                />
              </F>

              <F label="Sender Contact / Phone">
                <Input
                  value={pt.consignor_phone || ''}
                  onChange={e => updateFields({ party_details: { consignor_phone: e.target.value } })}
                  placeholder="Contact person & mobile number"
                  className="h-8 text-xs border-gray-300 font-ibm bg-white"
                />
              </F>
            </div>

            {/* Consignee / Receiver (TO) */}
            <div className="space-y-3 p-4 bg-blue-50/30 rounded-xl border border-blue-100">
              <div className="flex items-center justify-between pb-2 border-b border-blue-200/60">
                <span className="text-xs font-bold text-blue-900 font-ibm uppercase tracking-wide">
                  To: Consignee / Receiver
                </span>
                <span className="text-[10px] text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded font-medium">Destination</span>
              </div>

              <F label="Consignee Name" required>
                <CustomerCombobox
                  items={customers}
                  value={pt.consignee || h.importer_receiver || ''}
                  onSelect={item => {
                    const cid = item ? item.id : '';
                    if (onCustomerSelect && cid) onCustomerSelect(cid, 'consignee');
                    updateFields({
                      party_details: {
                        consignee: cid,
                        consignee_name: item ? item.business_name : '',
                        consignee_branch: '',
                        delivery_address: '',
                      },
                      header: {
                        importer_receiver: cid,
                        importer_receiver_branch: '',
                      }
                    });
                  }}
                  placeholder="Select Receiver / Consignee"
                />
              </F>

              {/* Branch Selector */}
              {getBranches(pt.consignee || h.importer_receiver).length > 0 && (
                <F label="Branch / Facility">
                  <Select
                    value={pt.consignee_branch || h.importer_receiver_branch || 'none'}
                    onValueChange={v => {
                      const bid = v === 'none' ? '' : v;
                      const branches = customerDetails[pt.consignee || h.importer_receiver]?.branches || [];
                      const b = branches.find(x => x.id === bid);
                      const addr = b ? [b.address_line_1, b.address_line_2, b.city, b.state, b.pincode, b.country].filter(Boolean).join(', ') : pt.delivery_address;
                      updateFields({
                        party_details: {
                          consignee_branch: bid,
                          delivery_address: addr || pt.delivery_address,
                          consignee_phone: b?.contact_person ? `${b.contact_person} (${b.contact_numbers || ''})` : pt.consignee_phone
                        },
                        header: { importer_receiver_branch: bid }
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Default Head Office --</SelectItem>
                      {getBranches(pt.consignee || h.importer_receiver).map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} ({b.subtext})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
              )}

              <F label="Delivery / Receiver Address">
                <Textarea
                  rows={2}
                  value={pt.delivery_address || ''}
                  onChange={e => updateFields({ party_details: { delivery_address: e.target.value } })}
                  placeholder="Full delivery street address, city, pin code..."
                  className="text-xs border-gray-300 font-ibm resize-none bg-white"
                />
              </F>

              <F label="Receiver Contact / Phone">
                <Input
                  value={pt.consignee_phone || ''}
                  onChange={e => updateFields({ party_details: { consignee_phone: e.target.value } })}
                  placeholder="Contact person & mobile number"
                  className="h-8 text-xs border-gray-300 font-ibm bg-white"
                />
              </F>
            </div>
          </div>

          {/* Bill To Customer */}
          <div className="pt-2 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Bill To (Billing Account)" hint="Invoicing party">
                <CustomerCombobox
                  items={customers}
                  value={pt.bill_to || h.bill_to || ''}
                  onSelect={item => {
                    const cid = item ? item.id : '';
                    if (onCustomerSelect && cid) onCustomerSelect(cid, 'bill_to');
                    updateFields({
                      party_details: { bill_to: cid, bill_to_branch: '' },
                      header: { bill_to: cid, bill_to_branch: '' }
                    });
                  }}
                  placeholder="Same as Shipper (or select client)"
                />
              </F>
              {getBranches(pt.bill_to || h.bill_to).length > 0 && (
                <F label="Billing Branch / GST State">
                  <Select
                    value={pt.bill_to_branch || h.bill_to_branch || 'none'}
                    onValueChange={v => {
                      const bid = v === 'none' ? '' : v;
                      updateFields({
                        party_details: { bill_to_branch: bid },
                        header: { bill_to_branch: bid }
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Default Branch --</SelectItem>
                      {getBranches(pt.bill_to || h.bill_to).map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} ({b.subtext})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ─── SECTION 3: ROUTING (ORIGIN & DESTINATION) ──────────────────── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <SectionHeader
          icon={MapPin}
          color="border-emerald-500"
          title="Origin & Destination Routing"
          subtitle="Dispatch and Delivery Hubs"
        />
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Origin Hub / Loading City" required>
              <MasterCombobox
                items={locItems}
                value={ship.place_of_receipts || ship.loading_from || ''}
                onSelect={item => {
                  const lid = item ? item.id : '';
                  updateFields({
                    shipment_info: { place_of_receipts: lid, loading_from: lid }
                  });
                }}
                placeholder="Search origin location or city..."
              />
            </F>

            <F label="Destination Hub / Delivery City" required>
              <MasterCombobox
                items={locItems}
                value={ship.delivery_at || ship.discharge_at || ''}
                onSelect={item => {
                  const lid = item ? item.id : '';
                  updateFields({
                    shipment_info: { delivery_at: lid, discharge_at: lid }
                  });
                }}
                placeholder="Search destination location or city..."
              />
            </F>
          </div>
        </div>
      </Card>

      {/* ─── SECTION 4: PACKAGE & SHIPMENT DETAILS ──────────────────────── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <SectionHeader
          icon={Tag}
          color="border-purple-500"
          title="Package & Cargo Details"
          subtitle="Pieces, package type, weight and cargo classification"
        />
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Package Type */}
            <F label="Package Type" required>
              <Select
                value={p.package_type || h.package_type || 'Parcel'}
                onValueChange={v => {
                  updateFields({
                    package_details: { package_type: v },
                    header: { package_type: v }
                  });
                }}
              >
                <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COURIER_PKG_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>

            {/* No of Pieces */}
            <F label="No. of Pkgs" required>
              <Input
                type="number"
                min="1"
                data-testid="courier-pkgs-input"
                value={p.no_of_packages || pInfo.package_no_of_packages || 1}
                onChange={e => {
                  const val = parseInt(e.target.value, 10) || 1;
                  updateFields({
                    package_details: { no_of_packages: val },
                    package_info: { package_no_of_packages: val }
                  });
                }}
                className="h-8 text-xs border-gray-300 font-ibm font-bold"
              />
            </F>

            {/* Actual / Gross Weight */}
            <F label="Gross Wt (KG)" required hint="Actual wt">
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="courier-gross-wt-input"
                value={p.actual_weight || pInfo.package_gross_weight || ''}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  updateFields({
                    package_details: { actual_weight: val, charge_weight: p.charge_weight || val },
                    package_info: { package_gross_weight: val, package_cbm_chargeable_weight: pInfo.package_cbm_chargeable_weight || val }
                  });
                }}
                placeholder="0.00"
                className="h-8 text-xs border-gray-300 font-ibm font-bold"
              />
            </F>

            {/* Chargeable Weight */}
            <F label="Charge Wt (KG)" required hint="Billing wt">
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="courier-charge-wt-input"
                value={p.charge_weight || pInfo.package_cbm_chargeable_weight || ''}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  updateFields({
                    package_details: { charge_weight: val },
                    package_info: { package_cbm_chargeable_weight: val }
                  });
                }}
                placeholder="0.00"
                className="h-8 text-xs border-gray-300 font-ibm font-bold"
              />
            </F>

            {/* Cargo Classification */}
            <F label="Cargo Nature">
              <Select
                value={h.cargo_type || 'General'}
                onValueChange={v => updateFields({ header: { cargo_type: v } })}
              >
                <SelectTrigger className="h-8 text-xs border-gray-300 font-ibm bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARGO_NATURE_OPTIONS.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>

            {/* Declared Value */}
            <F label="Declared Val (₹)" hint="Customs/Insurance">
              <Input
                type="number"
                min="0"
                value={iv.declared_value || ''}
                onChange={e => updateFields({ invoice_details: { declared_value: e.target.value } })}
                placeholder="₹ 0.00"
                className="h-8 text-xs border-gray-300 font-ibm"
              />
            </F>
          </div>

          {/* Description of Goods */}
          <F label="Description / Content of Goods" required hint="Appears on Consignment Note">
            <Input
              value={p.description_of_goods || (payload.invoices?.[0]?.description) || ''}
              onChange={e => {
                const val = e.target.value;
                const invList = payload.invoices || [{}];
                invList[0] = { ...(invList[0] || {}), description: val };
                updateFields({
                  package_details: { description_of_goods: val },
                  invoices: invList
                });
              }}
              placeholder="e.g. Commercial Documents / Sample Spare Parts / Diagnostic Reagents..."
              className="h-8 text-xs border-gray-300 font-ibm"
            />
          </F>
        </div>
      </Card>

      {/* ─── SECTION 5: INVOICE & COMMERCIAL DETAILS (OPTIONAL) ─────────── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <SectionHeader
          icon={FileText}
          color="border-cyan-500"
          title="Invoice & Commercial Details (Optional)"
          subtitle="Tax invoice, E-Way bill and commercial value"
        />
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <F label="Invoice Number">
              <Input
                value={iv.invoice_no || ''}
                onChange={e => {
                  const val = e.target.value;
                  const invList = payload.invoices || [{}];
                  invList[0] = { ...(invList[0] || {}), invoice_number: val };
                  updateFields({
                    invoice_details: { invoice_no: val },
                    invoices: invList
                  });
                }}
                placeholder="INV-2026-001"
                className="h-8 text-xs border-gray-300 font-ibm"
              />
            </F>

            <F label="Invoice Date">
              <Input
                type="date"
                value={iv.invoice_date || ''}
                onChange={e => {
                  const val = e.target.value;
                  const invList = payload.invoices || [{}];
                  invList[0] = { ...(invList[0] || {}), invoice_date: val };
                  updateFields({
                    invoice_details: { invoice_date: val },
                    invoices: invList
                  });
                }}
                className="h-8 text-xs border-gray-300 font-ibm"
              />
            </F>

            <F label="Invoice Value (₹)">
              <Input
                type="number"
                min="0"
                value={iv.invoice_value || ''}
                onChange={e => {
                  const val = e.target.value;
                  const invList = payload.invoices || [{}];
                  invList[0] = { ...(invList[0] || {}), invoice_value: val };
                  updateFields({
                    invoice_details: { invoice_value: val },
                    invoices: invList
                  });
                }}
                placeholder="0.00"
                className="h-8 text-xs border-gray-300 font-ibm"
              />
            </F>

            <F label="E-Way Bill Number">
              <Input
                value={iv.eway_bill_no || ''}
                onChange={e => {
                  const val = e.target.value;
                  const invList = payload.invoices || [{}];
                  invList[0] = { ...(invList[0] || {}), eway_bill_no: val };
                  updateFields({
                    invoice_details: { eway_bill_no: val },
                    invoices: invList
                  });
                }}
                placeholder="12-digit E-Way Bill"
                className="h-8 text-xs border-gray-300 font-mono"
              />
            </F>
          </div>
        </div>
      </Card>

      {/* ─── SECTION 6: HANDLING NOTES & SPECIAL INSTRUCTIONS ───────────── */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <SectionHeader
          icon={Info}
          color="border-slate-500"
          title="Handling Instructions & Delivery Notes"
        />
        <div className="p-5">
          <F label="Special Instructions / Delivery Remarks">
            <Textarea
              rows={2}
              value={h.remarks || p.remarks || ''}
              onChange={e => {
                const val = e.target.value;
                updateFields({
                  header: { remarks: val },
                  package_details: { remarks: val }
                });
              }}
              placeholder="e.g. Handle with Care / Deliver to 4th floor reception / Call recipient before delivery..."
              className="text-xs border-gray-300 font-ibm resize-none bg-white"
            />
          </F>
        </div>
      </Card>
    </div>
  );
};

export default CourierDocketForm;
